import React, { useEffect, useState, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════
//  AnalyticsPanel — the "Аналитика" tab
//
//  Four read-only REST endpoints:
//    GET /api/analytics/summary  — top-level counters
//    GET /api/analytics/tasks    — recent task rows with telemetry
//    GET /api/analytics/phases   — per-phase progress + tokens
//    GET /api/tasks/:id/telemetry — expanded per-task detail
//
//  Cost USD is intentionally NOT rendered yet — only token counts
//  and modules/commits/files. We surface it later once pricing
//  surfaces are agreed upon.
// ═══════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface Summary {
  tokens: { input: number; output: number; total: number };
  tasks: { total: number; completed: number; failed: number; running: number; pending: number };
  modules: { total: number; pending: number; in_progress: number; self_check: number; completed: number; needs_fix: number; failed: number };
  phases: { total: number; in_progress: number; review: number; accepted: number; rejected: number };
  commits: { totalHashes: number; filesTouched: number; mergeRequests: number };
  activity: { workers: number; activeWorkers: number; runs24h: number; runs7d: number };
}

interface TaskRow {
  taskId: string;
  workerName: string;
  title: string;
  module: string;
  phaseName?: string;
  status: string;
  durationSec?: number;
  inputTokens: number;
  outputTokens: number;
  filesCount: number;
  commitsCount: number;
  toolUsesCount: number;
  model?: string;
}

interface ModelTokenSlice {
  model: string;
  tasks: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  costUsdEstimated: number;
}

interface PhaseRow {
  phaseId: string;
  name: string;
  status: string;
  modulesTotal: number;
  modulesDone: number;
  modulesPending: number;
  inputTokens: number;
  outputTokens: number;
  totalCommits: number;
  mrUrl?: string;
  // Token economy (added — old backend won't return these, so the UI
  // tolerates undefined and falls back to "—").
  costUsd?: number;
  costUsdEstimated?: number;
  tasksRun?: number;
  modelBreakdown?: ModelTokenSlice[];
}

interface TaskDetail {
  taskId: string;
  inputTokens: number;
  outputTokens: number;
  toolUses: Array<{ name: string; input: string }>;
  filesTouched: string[];
  commitHashes: string[];
  model?: string;
  durationMs?: number;
  turnCount: number;
}

const fmt = (n: number) => n.toLocaleString('ru-RU');
const shortSha = (s: string) => (s || '').substring(0, 7);

// Render a USD amount with sensible precision: full cents above $1,
// 4 decimals (~tenth of a cent) for low-cost runs so we don't show
// "$0.00" for a $0.0042 task.
const formatUsd = (n: number): string => {
  if (n <= 0) return '$0.00';
  return n >= 1
    ? `$${n.toFixed(2)}`
    : `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')}`;
};

export const AnalyticsPanel: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [phases, setPhases] = useState<PhaseRow[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, t, p] = await Promise.all([
        fetch(`${API}/api/analytics/summary`).then(r => r.json()),
        fetch(`${API}/api/analytics/tasks?limit=80`).then(r => r.json()),
        fetch(`${API}/api/analytics/phases`).then(r => r.json()),
      ]);
      if (!s.error) setSummary(s);
      if (Array.isArray(t)) setTasks(t);
      if (Array.isArray(p)) setPhases(p);
    } catch (err) {
      console.error('[Analytics] load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // poll every 8s
    return () => clearInterval(t);
  }, [load]);

  const openTask = async (taskId: string) => {
    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
      setSelectedTask(null);
      return;
    }
    setSelectedTaskId(taskId);
    try {
      const detail = await fetch(`${API}/api/tasks/${taskId}/telemetry`).then(r => r.json());
      if (!detail.error) setSelectedTask(detail);
    } catch {}
  };

  if (loading && !summary) {
    return <div className="analytics-loading">Загружаю аналитику…</div>;
  }

  return (
    <div className="analytics-panel">
      {/* ─── Summary cards ─── */}
      {summary && (
        <div className="an-grid">
          <Card label="Токены in / out / total"
                value={`${fmt(summary.tokens.input)} / ${fmt(summary.tokens.output)} / ${fmt(summary.tokens.total)}`}
                accent="#a78bfa" />
          <Card label="Задачи (done / running / failed)"
                value={`${summary.tasks.completed} / ${summary.tasks.running} / ${summary.tasks.failed}`}
                accent="#22c55e" />
          <Card label="Модули (done / ост / fix)"
                value={`${summary.modules.completed} / ${summary.modules.total - summary.modules.completed - summary.modules.failed} / ${summary.modules.needs_fix}`}
                accent="#06b6d4" />
          <Card label="Фазы (всего / ревью / принято)"
                value={`${summary.phases.total} / ${summary.phases.review} / ${summary.phases.accepted}`}
                accent="#f97316" />
          <Card label="Коммиты / файлы / MR"
                value={`${summary.commits.totalHashes} / ${summary.commits.filesTouched} / ${summary.commits.mergeRequests}`}
                accent="#eab308" />
          <Card label="Активность (24h / 7d / воркеров)"
                value={`${summary.activity.runs24h} / ${summary.activity.runs7d} / ${summary.activity.activeWorkers} из ${summary.activity.workers}`}
                accent="#ef4444" />
        </div>
      )}

      {/* ─── Phases ─── */}
      <div className="an-section-head">Расход на фазу — токены и стоимость</div>
      <div className="an-phases">
        {phases.length === 0 && <div className="an-empty">Пока нет загруженных фаз — загрузи .md на вкладке «Задачи».</div>}
        {phases.map((p) => {
          const pct = p.modulesTotal ? Math.round((p.modulesDone / p.modulesTotal) * 100) : 0;
          // Prefer CLI-reported cost; fall back to our priced
          // estimate when the CLI didn't fill in cost_usd.
          const cost = (p.costUsd && p.costUsd > 0) ? p.costUsd : (p.costUsdEstimated || 0);
          const costLabel = cost > 0 ? formatUsd(cost) : '—';
          const costSource = (p.costUsd && p.costUsd > 0) ? 'факт CLI' : (p.costUsdEstimated ? 'оценка по прайсу' : '');
          return (
            <div key={p.phaseId} className="an-phase">
              <div className="an-phase-head">
                <div>
                  <div className="an-phase-name">{p.name}</div>
                  <div className={`an-phase-status an-status-${p.status}`}>{p.status}</div>
                </div>
                <div className="an-phase-right">
                  <span>{p.modulesDone} / {p.modulesTotal} модулей</span>
                  <span className="an-phase-tokens">
                    {fmt(p.inputTokens + p.outputTokens)} токенов · {p.totalCommits} коммитов
                  </span>
                  {p.mrUrl && <a href={p.mrUrl} target="_blank" rel="noreferrer" className="an-mr-link">MR ↗</a>}
                </div>
              </div>
              <div className="an-progress-track">
                <div className="an-progress-fill" style={{ width: `${pct}%` }} />
              </div>

              {/* Token + cost breakdown — only render when the
                  backend provided the numbers (old backends won't). */}
              {(p.tasksRun !== undefined || p.modelBreakdown) && (
                <div className="an-phase-economy">
                  <div className="an-phase-econ-row">
                    <span className="an-phase-econ-key">Запусков</span>
                    <span className="an-phase-econ-val">{p.tasksRun ?? 0}</span>
                  </div>
                  <div className="an-phase-econ-row">
                    <span className="an-phase-econ-key">Input → Output</span>
                    <span className="an-phase-econ-val">
                      {fmt(p.inputTokens)} → {fmt(p.outputTokens)}
                    </span>
                  </div>
                  <div className="an-phase-econ-row">
                    <span className="an-phase-econ-key">Стоимость</span>
                    <span className="an-phase-econ-val an-phase-cost">
                      {costLabel}
                      {costSource && <span className="an-phase-cost-source"> · {costSource}</span>}
                    </span>
                  </div>
                  {p.modelBreakdown && p.modelBreakdown.length > 0 && (
                    <div className="an-phase-models">
                      <div className="an-phase-models-head">По моделям</div>
                      <table className="an-phase-models-tbl">
                        <thead>
                          <tr>
                            <th>Модель</th>
                            <th>Задач</th>
                            <th>Input</th>
                            <th>Output</th>
                            <th>$ факт</th>
                            <th>$ оценка</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.modelBreakdown.map((m) => (
                            <tr key={m.model}>
                              <td className="an-phase-models-name">{m.model}</td>
                              <td>{m.tasks}</td>
                              <td>{fmt(m.inputTokens)}</td>
                              <td>{fmt(m.outputTokens)}</td>
                              <td>{m.costUsd > 0 ? formatUsd(m.costUsd) : '—'}</td>
                              <td>{m.costUsdEstimated > 0 ? formatUsd(m.costUsdEstimated) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Recent tasks ─── */}
      <div className="an-section-head">Последние задачи — что воркер выбрал делать</div>
      <div className="an-tasks-wrap">
        <table className="an-tasks">
          <thead>
            <tr>
              <th>Воркер</th>
              <th>Модуль / таск</th>
              <th>Фаза</th>
              <th>Статус</th>
              <th>Длительность</th>
              <th>Токены (in→out)</th>
              <th>Инструменты</th>
              <th>Файлов</th>
              <th>Коммитов</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && (
              <tr><td colSpan={9} className="an-empty">Пока ни одна задача не запускалась.</td></tr>
            )}
            {tasks.map((t) => {
              const active = selectedTaskId === t.taskId;
              return (
                <React.Fragment key={t.taskId}>
                  <tr className={`an-row an-row-${t.status} ${active ? 'an-row-open' : ''}`} onClick={() => openTask(t.taskId)}>
                    <td>{t.workerName}</td>
                    <td>
                      <div className="an-task-title">{t.title}</div>
                      <div className="an-task-module">{t.module}</div>
                    </td>
                    <td>{t.phaseName || '—'}</td>
                    <td><span className={`an-chip an-chip-${t.status}`}>{t.status}</span></td>
                    <td>{t.durationSec ? `${t.durationSec}s` : '—'}</td>
                    <td>{fmt(t.inputTokens)} → {fmt(t.outputTokens)}</td>
                    <td>{t.toolUsesCount}</td>
                    <td>{t.filesCount}</td>
                    <td>{t.commitsCount}</td>
                  </tr>
                  {active && selectedTask && selectedTask.taskId === t.taskId && (
                    <tr className="an-detail-row">
                      <td colSpan={9}>
                        <div className="an-detail">
                          <div className="an-detail-col">
                            <div className="an-detail-head">Использованные инструменты ({selectedTask.toolUses.length})</div>
                            {selectedTask.toolUses.slice(-15).map((u, i) => (
                              <div key={i} className="an-detail-line">
                                <span className="an-tool-name">{u.name}</span>
                                <span className="an-tool-input">{u.input}</span>
                              </div>
                            ))}
                            {selectedTask.toolUses.length === 0 && <div className="an-empty">—</div>}
                          </div>
                          <div className="an-detail-col">
                            <div className="an-detail-head">Изменённые файлы ({selectedTask.filesTouched.length})</div>
                            {selectedTask.filesTouched.slice(0, 30).map((f) => (
                              <div key={f} className="an-detail-line an-file">{f}</div>
                            ))}
                            {selectedTask.filesTouched.length === 0 && <div className="an-empty">—</div>}
                          </div>
                          <div className="an-detail-col">
                            <div className="an-detail-head">Коммиты ({selectedTask.commitHashes.length})</div>
                            {selectedTask.commitHashes.map((h) => (
                              <div key={h} className="an-detail-line an-commit"><code>{shortSha(h)}</code></div>
                            ))}
                            {selectedTask.commitHashes.length === 0 && <div className="an-empty">—</div>}
                            <div className="an-detail-meta">
                              {selectedTask.model && <div>модель: <code>{selectedTask.model}</code></div>}
                              <div>turns: {selectedTask.turnCount}</div>
                              {selectedTask.durationMs && <div>CLI: {Math.round(selectedTask.durationMs / 1000)}s</div>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Card: React.FC<{ label: string; value: string; accent: string }> = ({ label, value, accent }) => (
  <div className="an-card" style={{ borderColor: accent + '55' }}>
    <div className="an-card-label">{label}</div>
    <div className="an-card-value" style={{ color: accent }}>{value}</div>
  </div>
);
